import { ocularMotilityInstruction } from "@constants/instructionConstants";
import { virtualAssistanceOcularMotilityConfig } from "@constants/virtualAssistanceConstants";
import {
  OMTestCustomConfiguration,
  AntiSaccadesCustomConfiguration,
  AvailablePreTestConfigurationOptions,
  TestType,
  EyeType,
  TestConfiguration,
  TestCategory,
  WaitDuration,
  TestUIConfiguration,
} from "types/testTypes";
import {
  CATEGORY_VERSION_MAP,
  FEATURE_VERSION_MAP,
  TEST_TYPE_VERSION_MAP,
} from "../versionConstants";
import {
  AVAILABLE_EYE_OPTIONS_BINOCULAR,
  AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS,
  CALIBRATION_TOGGLE_OPTION_NEW_APK,
  DUMMY_AVAILABLE_TEST_SPECIFIC_CONFIGURATION_OPTIONS,
} from "../configurationHelperConstants";

export const availableOcularMotilityTestConfigurationOptions: AvailablePreTestConfigurationOptions =
  {
    testTypes: {
      choices: [
        { validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.XIH], value: TestType.XIH },
        {
          validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.SmoothPursuit],
          value: TestType.SmoothPursuit,
        },
        {
          validSinceVersion: TEST_TYPE_VERSION_MAP[TestType.AntiSaccades],
          value: TestType.AntiSaccades,
        },
      ],
      isMandatory: true,
      validSinceVersion: CATEGORY_VERSION_MAP[TestCategory.OcularMotility],
      configurationFieldName: "testType",
    },
    eyeConfigurationOptions: {
      ...AVAILABLE_EYE_OPTIONS_BINOCULAR,
      toggleCalibrationOption: { ...CALIBRATION_TOGGLE_OPTION_NEW_APK },
    },
    languageOptions: null,
    virtualAssistantOptions: { ...AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS },
    testSpeedOptions: null,
    testSpecificConfigurationOptions: {
      ...DUMMY_AVAILABLE_TEST_SPECIFIC_CONFIGURATION_OPTIONS,
      trialCountOptions: {
        choices: [{ value: 16 }, { value: 32 }],
        validSinceVersion: null,
        configurationFieldName: "trialCount",
      },
      toggleChildMode: {
        choices: null,
        validSinceVersion: FEATURE_VERSION_MAP.smoothPursuitChildMode,
        configurationFieldName: "childMode",
      },
    },
  };

export const ocularMotilityTestConfiguration: TestConfiguration = {
  name: TestCategory.OcularMotility,
  testType: TestType.XIH,
  eyeConfiguration: {
    eyeType: EyeType.BothEye,
    isCalibrationNeeded: true,
    autoPauseForGaze: false,
    isTwoScreenFixationEnabled: true,
    isBackToBackEyeTest: false,
  },
  testSpeed: WaitDuration.Standard,
  testLanguage: null,
  virtualAssistanceConfig: virtualAssistanceOcularMotilityConfig,
  isPressToStartEnabled: true,
  extraData: null,
};

export const ocularMotilityUIConfiguration: TestUIConfiguration = {
  showProgressBar: true,
  instructions: ocularMotilityInstruction,
};

export const omConfigXIH: OMTestCustomConfiguration = {
  // Half-extent 1.6 Unity units on the 3-unit plane: cardinal targets at ~28
  // degrees and the pattern corners at ~36, inside the conventional limits of
  // normal duction in every gaze direction and the headset tracker's reliable
  // range.
  patternSize: 3.2,
  speed: 5,
  loopCount: 1,
  pauseDuration: 0.5,
};

export const omConfigAntiSaccades: AntiSaccadesCustomConfiguration = {
  trialCount: 32,
  fixationDurationMinSeconds: 1.5,
  fixationDurationMaxSeconds: 2.5,
  interTrialMinSeconds: 1.0,
  interTrialMaxSeconds: 2.0,
  peripheralVisibleBeforeGuideSeconds: 1.0,
  guidingMoveToCenterDurationSeconds: 1.5,
  minHorizontalDegrees: -30.0,
  maxHorizontalDegrees: 30.0,
  screenDistanceMeters: 1.5,
};

export const omConfigCircularPursuit: OMTestCustomConfiguration = {
  patternSize: 8,
  speed: 1,
  loopCount: 2,
  pauseDuration: 0.5,
};
